import axios from 'axios'
import { baseUrlFrontApi } from '~/js/templates/index/helpers'
import {
  CONTENT_TITLES,
  REGIONS,
  BRANDS,
  PARAMS_VALIDATORS,
  normalizeBrand,
} from './constants'
import { createApp } from 'vue'
import { getParam } from '~/js/helpers/url-helper'
import { getQueryParams, getUrlWithQueryParams } from '~/js/helpers/url-helper'

import FBreadcrumbs from '~/js/components/breadcrumbs'

const ID_PAGES_STORES = 'js-pages-stores'
const MESSAGE_NOT_FOUND = '条件に当てはまる店舗はありませんでした。'
const MESSAGE_NO_QUERY =
  'エリアを選択するか、検索キーワードを入力してください。'
const MESSAGE_ERROR_OCCURRED =
  'エラーが発生しました。しばらくしてからもう一度お試しください。'

class Store {
  constructor(data = {}) {
    Object.keys(data).forEach((key) => {
      this[key] = key === 'brand' ? normalizeBrand(data[key]) : data[key]
    })
  }

  get formattedDetails() {
    return (
      this.details
        .split('\n')
        .map((line) =>
          // URLをリンク化
          line.replace(
            /https?:\/\/[^\s]+/g,
            (url) => `<a
            onclick="
            event.stopPropagation();
            event.preventDefault();
            window.open('${url}', '_blank', 'noopener,noreferrer');
            "
          >
            ${url}
          </a>
        `
          )
        )
        // 改行を<br/>に変換
        .join('<br/>')
    )
  }
}

const fetchStoresIndex = async (params) => {
  if (Object.keys(params).length === 0) return []
  const response = await axios.get(
    `${baseUrlFrontApi}/store-locator/stores?${getQueryParams(params, true)}`
  )
  return response.data.map((store) => new Store(store))
}

const fetchStoreDetail = async (storeId) => {
  const response = await axios.get(
    `${baseUrlFrontApi}/store-locator/stores/${storeId}`
  )
  return new Store(response.data)
}

const getParameters = () => {
  const params = {}
  Object.keys(PARAMS_VALIDATORS).forEach((key) => {
    const value =
      key === 'brand' ? normalizeBrand(getParam(key)) : getParam(key)
    if (PARAMS_VALIDATORS[key](value)) params[key] = value
  })
  return params
}

const getRegion = (prefecture) =>
  Object.keys(REGIONS).find(
    (region) => REGIONS[region].includes(prefecture) || null
  )

document.addEventListener('DOMContentLoaded', async () => {
  const elementPagesStores = document.getElementById(ID_PAGES_STORES)
  if (!elementPagesStores) return

  const params = getParameters()
  let errorMessage = ''
  let storeDetail = null
  let storeList = []
  try {
    const p = {}
    if (params.store) {
      storeDetail = await fetchStoreDetail(params.store)
      p.store = params.store
    } else if (params.brand || params.prefecture || params.region || params.q) {
      if (params.q) p.q = params.q
      else if (params.prefecture) p.prefecture = params.prefecture
      else if (params.region) p.region = params.region
      if ((params.prefecture || params.region) && params.brand)
        p.brand = params.brand

      storeList = await fetchStoresIndex(p)
    }
    const url = getUrlWithQueryParams(p, true)
    history.replaceState({}, '', url)
  } catch (error) {
    errorMessage =
      error?.response?.status === 404
        ? MESSAGE_NOT_FOUND
        : MESSAGE_ERROR_OCCURRED
    console.error(error)
  }

  createApp({
    name: 'PageStores',
    components: { FBreadcrumbs },
    data() {
      return {
        errorMessage,
        isProcessing: false,
        isSearchPending: true,
        searchKeyword: params.store ? '' : params.q || '',
        selectedBrand: params.store ? '' : params.brand || '',
        selectedPrefecture: params.store ? '' : params.prefecture || '',
        selectedRegion: params.store
          ? ''
          : params.prefecture
          ? getRegion(params.prefecture)
          : params.region || '',
        selectedStoreId: params.store || '',
        storeList,
        storeDetail,
      }
    },
    watch: {
      searchKeyword() {
        this.isSearchPending = true
      },
    },
    created() {
      window.addEventListener('popstate', () => {
        this.syncParameters()
      })
    },
    computed: {
      brands() {
        return BRANDS
      },
      regions() {
        return REGIONS
      },
      prefectures() {
        if (this.selectedRegion) return REGIONS[this.selectedRegion]
        const prefectures = Object.values(REGIONS).find((prefectures) => {
          return prefectures.includes(this.selectedPrefecture)
        })
        return prefectures || []
      },
      storeListGrouped() {
        return this.prefectures
          .map((prefecture) => {
            return {
              prefecture,
              stores: this.storeList.filter(
                (store) => store.prefecture === prefecture
              ),
            }
          })
          .concat([
            {
              prefecture: '',
              stores: this.storeList.filter((store) =>
                this.prefectures.every((p) => p !== store.prefecture)
              ),
            },
          ])
          .filter((group) => !!group.stores.length)
      },
      message() {
        if (this.errorMessage) return this.errorMessage

        if (
          !this.selectedBrand &&
          !this.selectedPrefecture &&
          !this.selectedRegion &&
          (!this.searchKeyword || this.isSearchPending) &&
          !this.selectedStoreId
        )
          return MESSAGE_NO_QUERY

        if (this.storeList.length === 0 && !this.storeDetail)
          return MESSAGE_NOT_FOUND
        return ''
      },
      breadcrumbs() {
        const breadcrumbs = [
          { text: 'TOP', href: '/' },
          { text: 'SHOP LIST', href: getUrlWithQueryParams({}, true) },
        ]

        if (this.selectedRegion)
          breadcrumbs.push({
            text: this.selectedRegion,
            href: getUrlWithQueryParams({ region: this.selectedRegion }, true),
          })

        if (this.selectedPrefecture) {
          if (!this.selectedRegion) {
            const region = getRegion(this.selectedPrefecture)
            breadcrumbs.push({
              text: region,
              href: getUrlWithQueryParams({ region }, true),
            })
          }

          breadcrumbs.push({
            text: this.selectedPrefecture,
            href: getUrlWithQueryParams(
              { prefecture: this.selectedPrefecture },
              true
            ),
          })
        }

        if (this.selectedBrand)
          breadcrumbs.push({
            text: this.selectedBrand,
            href: getUrlWithQueryParams({ brand: this.selectedBrand }, true),
          })

        if (this.storeDetail) {
          const prefecture = this.storeDetail.prefecture
          const region = getRegion(prefecture)
          breadcrumbs.push({
            text: region,
            href: getUrlWithQueryParams({ region }, true),
          })
          breadcrumbs.push({
            text: prefecture,
            href: getUrlWithQueryParams({ prefecture }, true),
          })
          breadcrumbs.push({
            text: this.storeDetail.name,
          })
        }

        return breadcrumbs
      },
    },
    methods: {
      async setList(shouldPushState = true) {
        this.storeList = []
        this.selectedStoreId = ''
        this.storeDetail = null
        this.isProcessing = true
        this.isSearchPending = false
        const queries = {
          brand: this.selectedBrand || undefined,
          prefecture: this.selectedPrefecture || undefined,
          region: this.selectedPrefecture
            ? undefined
            : this.selectedRegion || undefined,
          q: this.searchKeyword || undefined,
        }
        try {
          window.scrollTo({ top: 0, behavior: 'smooth' })
          this.errorMessage = ''
          this.storeList = await fetchStoresIndex(queries)
          if (shouldPushState) {
            const url = getUrlWithQueryParams(queries, true)
            history.pushState(CONTENT_TITLES.list, '', url)
          }
          this.isProcessing = false
        } catch (error) {
          console.error(error)
          this.errorMessage = MESSAGE_ERROR_OCCURRED
          this.isProcessing = false
        }
      },
      async setDetail(shouldPushState = true) {
        this.storeDetail = null
        this.storeList = []
        this.selectedBrand = ''
        this.selectedPrefecture = ''
        this.selectedRegion = ''
        this.searchKeyword = ''
        this.isProcessing = true
        try {
          window.scrollTo({ top: 0, behavior: 'smooth' })
          this.errorMessage = ''
          this.storeDetail = await fetchStoreDetail(this.selectedStoreId)
          if (shouldPushState) {
            const url = getUrlWithQueryParams(
              { store: this.selectedStoreId },
              true
            )
            history.pushState(CONTENT_TITLES.list, '', url)
          }
          this.isProcessing = false
        } catch (error) {
          this.errorMessage = MESSAGE_ERROR_OCCURRED
          this.isProcessing = false
        }
      },
      async brandChanged() {
        this.setList()
      },
      async searchByKeyword() {
        if (!this.searchKeyword.trim()) return
        this.selectedRegion = ''
        this.selectedPrefecture = ''
        this.selectedBrand = ''
        this.setList()
      },
      searchKeywordEntered($event) {
        if (!$event.isComposing) this.searchByKeyword()
      },
      async regionClicked(region) {
        const selectedRegion = region || getRegion(this.selectedPrefecture)
        if (!selectedRegion) return
        this.selectedRegion = selectedRegion
        this.searchKeyword = ''
        this.selectedPrefecture = ''
        this.setList()
      },
      async prefectureClicked(prefecture) {
        this.selectedPrefecture = prefecture
        this.selectedRegion = getRegion(prefecture)
        this.setList()
      },
      async detailClicked(storeId) {
        this.selectedStoreId = storeId
        this.setDetail()
      },
      syncParameters() {
        const params = getParameters()
        if (params.store) {
          this.selectedStoreId = params.store
          this.setDetail(false)
        } else {
          this.searchKeyword = params.q || ''
          this.selectedBrand = params.brand || ''
          this.selectedPrefecture = params.prefecture || ''
          this.selectedRegion =
            params.region || getRegion(params.prefecture) || ''
          this.setList(false)
        }
      },
    },
  }).mount(elementPagesStores)
})
